import {
  dashboardLayoutForUser,
  isDualRoleAccount,
  showBecomeAListenerCta,
  showFindAListenerCta,
  listeningProfileSetupRoute,
  talkingProfileSetupRoute,
} from './accountRole';
import { landingPageLinkTo } from './publicHome';
import {
  NAV_MODE_LISTENER,
  NAV_MODE_TALKER,
  clampNavModeToAccount,
  tipsSearchForNavMode,
} from './siteNavMode';

/**
 * Shared site destinations for the hamburger and the desktop header.
 * Terms and Privacy stay in the footer unless they are added here.
 */

export const PUBLIC_NAV_LINKS = [
  { name: 'LandingPage', id: 'SiteFooter.home' },
  { name: 'SearchPage', id: 'SiteFooter.search' },
  { name: 'SweetSkillsPage', id: 'SiteDrawer.skills' },
  { name: 'BecomeAListenerPage', id: 'SiteFooter.becomeListener' },
  { name: 'HowItWorksPage', id: 'SiteFooter.howItWorks' },
  { name: 'AboutPage', id: 'SiteFooter.about' },
  { name: 'SafetyPage', id: 'SiteFooter.safety' },
  { name: 'FAQPage', id: 'SiteFooter.faq' },
  { name: 'PricingPage', id: 'SiteFooter.pricing' },
  { name: 'ContactPage', id: 'SiteFooter.contact' },
  { name: 'PromotePage', id: 'SiteFooter.promote' },
  { name: 'GuidesPage', id: 'SiteFooter.guides' },
  { name: 'HelpPage', id: 'SiteFooter.help' },
];

/**
 * Marketing destinations that used to live in the desktop pill bar.
 * How it Works, About, FAQ. Role pages stay at /for-talkers and
 * /for-listeners and are linked from home + How it works cards only.
 */
export const GUEST_HEADER_NAV = [
  { name: 'HowItWorksPage', id: 'GuestTopbar.howItWorks' },
  { name: 'AboutPage', id: 'GuestTopbar.about' },
  { name: 'FAQPage', id: 'GuestTopbar.faq' },
];

/** Guest hamburger matches the homepage mock. Help stays last. */
const GUEST_MENU_ROUTES = new Set([
  'LandingPage',
  'SearchPage',
  'SweetSkillsPage',
  'BecomeAListenerPage',
  'HowItWorksPage',
  'AboutPage',
  'SafetyPage',
  'FAQPage',
  'PricingPage',
  'ContactPage',
  'HelpPage',
]);

/**
 * Slide-out drawer destinations from the homepage mock.
 * Become uses /l/new so guests land on the listening-profile flow.
 */
export const DRAWER_NAV_LINKS = [
  { name: 'LandingPage', id: 'SiteDrawer.home' },
  { name: 'SearchPage', id: 'SiteDrawer.find' },
  { name: 'SweetSkillsPage', id: 'SiteDrawer.skills' },
  { name: 'NewListingPage', id: 'SiteDrawer.become', become: true },
  { name: 'HowItWorksPage', id: 'SiteDrawer.howItWorks' },
  { name: 'AboutPage', id: 'SiteDrawer.about' },
  { name: 'SafetyPage', id: 'SiteDrawer.safety' },
  { name: 'FAQPage', id: 'SiteDrawer.faq' },
  { name: 'PricingPage', id: 'SiteDrawer.pricing' },
  { name: 'ContactPage', id: 'SiteDrawer.contact' },
  { name: 'HelpPage', id: 'SiteDrawer.support' },
];

/**
 * Mock drawer nav. Home uses ?home=1 when signed in. Become hides after
 * the account already has a listening profile.
 *
 * @param {Object} [params]
 * @returns {Array<{ name: string, id: string, to?: Object }>}
 */
export const getDrawerNavLinks = ({
  currentUser,
  currentUserHasListings = false,
  currentUserOwnListing = null,
  isAuthenticated,
} = {}) => {
  const signedIn = isAuthenticated === true || (isAuthenticated !== false && !!currentUser?.id);
  const showBecome = showBecomeAListenerCta({
    currentUser,
    currentUserHasListings,
    currentUserOwnListing,
    isAuthenticated: signedIn,
  });
  const showFind = showFindAListenerCta({
    currentUser,
    currentUserHasListings,
    currentUserOwnListing,
    isAuthenticated: signedIn,
  });
  return DRAWER_NAV_LINKS.filter(link => {
    if (link.become && !showBecome) {
      return false;
    }
    if (link.name === 'SearchPage' && !showFind) {
      return false;
    }
    return true;
  }).map(link => {
    if (link.name === 'LandingPage') {
      return { ...link, to: landingPageLinkTo({ isAuthenticated: signedIn }) };
    }
    return link;
  });
};

/**
 * Marketing pages to show in the desktop CustomLinksMenu (inline or More).
 * Signed-in Home is included so customers can still open the public homepage.
 *
 * @param {Object} [params]
 * @param {boolean} [params.isAuthenticated]
 * @returns {Array<{ name: string, id: string, to?: Object }>}
 */
export const getDesktopSiteNavLinks = ({ isAuthenticated } = {}) => {
  if (isAuthenticated === true) {
    return [
      { name: 'LandingPage', id: 'SiteFooter.home', to: landingPageLinkTo({ isAuthenticated }) },
      ...GUEST_HEADER_NAV,
    ];
  }
  return GUEST_HEADER_NAV;
};

/**
 * Public nav. Become a listener stays in the guest site list. Signed-in
 * accounts see it once, in the account section near Dashboard.
 *
 * @param {Object} [params]
 * @returns {Array<{ name: string, id: string }>}
 */
export const HELP_NAV_LINK = { name: 'HelpPage', id: 'SiteFooter.help' };

const PROFILE_CURRENT_PAGES = new Set([
  'YourProfilePage',
  'YourProfilePageTab',
  'ProfileSettingsPage',
]);
const ACCOUNT_CURRENT_PAGES = new Set([
  'AccountSettingsPage',
  'ManageAccountPage',
  'AccountProfilePage',
  'ContactDetailsPage',
  'PasswordChangePage',
  'StripePayoutPage',
  'PaymentMethodsPage',
]);

/**
 * True only for the open page. Empty names never match, so Pricing cannot
 * light up on Tips & Resources.
 *
 * @param {string} [currentPage]
 * @param {string} [page]
 * @returns {boolean}
 */
export const isDrawerCurrentPage = (currentPage, page) => {
  if (typeof currentPage !== 'string' || !currentPage || typeof page !== 'string' || !page) {
    return false;
  }
  if (currentPage === page) {
    return true;
  }
  if (page.indexOf('InboxPage') === 0 && currentPage.indexOf('InboxPage') === 0) {
    return true;
  }
  if (page === 'YourProfilePage' && PROFILE_CURRENT_PAGES.has(currentPage)) {
    return true;
  }
  if (page === 'AccountSettingsPage' && ACCOUNT_CURRENT_PAGES.has(currentPage)) {
    return true;
  }
  return false;
};

export const DASHBOARD_NAV_LINK = { name: 'DashboardPage', id: 'TopbarMobileMenu.dashboard' };

/**
 * Signed-in hamburger destinations for Talker or Listener mode.
 * Guest drawer stays on DRAWER_NAV_LINKS. No Messages item.
 *
 * @param {Object} [params]
 * @returns {Array<{ name: string, id: string, to?: Object, params?: Object, icon?: string }>}
 */
export const getSignedInDrawerNav = ({
  currentUser,
  currentUserHasListings = false,
  currentUserOwnListing = null,
  ownListings = [],
  mode,
} = {}) => {
  const layout = {
    currentUser,
    currentUserHasListings,
    currentUserOwnListing,
    ownListings,
  };
  const resolved = clampNavModeToAccount(mode, layout);
  if (resolved === NAV_MODE_LISTENER) {
    return [
      {
        name: 'DashboardPage',
        id: 'SiteDrawer.dashboard',
        to: { search: '?tab=listening' },
        icon: 'DashboardPage',
        group: 'primary',
      },
      {
        name: 'InboxPage',
        id: 'SiteDrawer.myBookings',
        params: { tab: 'sales' },
        icon: 'InboxPage',
        group: 'primary',
      },
      {
        name: 'AvailabilityPage',
        id: 'SiteDrawer.myAvailability',
        icon: 'AvailabilityPage',
        group: 'primary',
      },
      {
        name: 'YourProfilePage',
        id: 'SiteDrawer.myListeningProfile',
        to: { search: '?tab=listening' },
        icon: 'YourProfilePage',
        group: 'primary',
      },
      {
        name: 'PromotePage',
        id: 'SiteDrawer.promote',
        icon: 'PromotePage',
        group: 'primary',
      },
      {
        name: 'TipsPage',
        id: 'SiteDrawer.tipsResources',
        to: { search: tipsSearchForNavMode(NAV_MODE_LISTENER) },
        icon: 'TipsPage',
        group: 'tips',
      },
    ];
  }
  return [
    {
      name: 'DashboardPage',
      id: 'SiteDrawer.dashboard',
      to: { search: '?tab=talking' },
      icon: 'DashboardPage',
      group: 'primary',
    },
    {
      name: 'InboxPage',
      id: 'SiteDrawer.myBookings',
      params: { tab: 'orders' },
      icon: 'InboxPage',
      group: 'primary',
    },
    { name: 'SearchPage', id: 'SiteDrawer.find', icon: 'SearchPage', group: 'primary' },
    { name: 'SweetSkillsPage', id: 'SiteDrawer.skills', icon: 'SearchPage', group: 'primary' },
    {
      name: 'FavoritesPage',
      id: 'SiteDrawer.favorites',
      icon: 'Favorites',
      group: 'primary',
    },
    {
      name: 'YourProfilePage',
      id: 'SiteDrawer.myTalkingProfile',
      to: { search: '?tab=talking' },
      icon: 'YourProfilePage',
      group: 'primary',
    },
    {
      name: 'TipsPage',
      id: 'SiteDrawer.tipsResources',
      to: { search: tipsSearchForNavMode(NAV_MODE_TALKER) },
      icon: 'TipsPage',
      group: 'tips',
    },
  ];
};

/**
 * Signed-in hamburger site pages after Switch / Settings / Log out.
 * Identical for Listener, Talker, and single-role accounts. Same routes as
 * the guest drawer, without Find or Become.
 *
 * @returns {Array<{ name: string, id: string, to?: Object }>}
 */
export const getSignedInDrawerSiteLinks = () => [
  {
    name: 'LandingPage',
    id: 'SiteDrawer.home',
    icon: 'LandingPage',
    to: landingPageLinkTo({ isAuthenticated: true }),
  },
  { name: 'HowItWorksPage', id: 'SiteDrawer.howItWorks' },
  { name: 'SafetyPage', id: 'SiteDrawer.safety' },
  { name: 'FAQPage', id: 'SiteDrawer.faq' },
  { name: 'PricingPage', id: 'SiteDrawer.pricing' },
  { name: 'ContactPage', id: 'SiteDrawer.contact' },
  { name: 'HelpPage', id: 'SiteDrawer.help' },
];

/**
 * Become / switch item under the signed-in divider.
 *
 * @param {Object} [params]
 * @returns {{ kind: 'switch'|'becomeListener'|'becomeTalker', nextMode?: string, name?: string, id: string, to?: Object }|null}
 */
export const getSignedInDrawerAccountAction = ({
  currentUser,
  currentUserHasListings = false,
  currentUserOwnListing = null,
  ownListings = [],
  mode,
} = {}) => {
  const layout = {
    currentUser,
    currentUserHasListings,
    currentUserOwnListing,
    ownListings,
  };
  const resolved = clampNavModeToAccount(mode, layout);
  const { showListening, showTalking } = dashboardLayoutForUser(layout);
  if (isDualRoleAccount(layout) || (showListening && showTalking)) {
    return {
      kind: 'switch',
      nextMode: resolved === NAV_MODE_LISTENER ? NAV_MODE_TALKER : NAV_MODE_LISTENER,
      id:
        resolved === NAV_MODE_LISTENER
          ? 'SiteDrawer.switchToTalker'
          : 'SiteDrawer.switchToListener',
    };
  }
  if (showTalking && !showListening) {
    const listening = listeningProfileSetupRoute(layout);
    return {
      kind: 'becomeListener',
      name: listening.name,
      params: listening.params,
      to: listening.to,
      id: 'SiteDrawer.become',
    };
  }
  if (showListening && !showTalking) {
    const talking = talkingProfileSetupRoute();
    return {
      kind: 'becomeTalker',
      name: talking.name,
      id: 'SiteDrawer.becomeTalker',
      to: talking.to,
    };
  }
  return null;
};

/** Hamburger / All-menu sections. Help stays last, not inside MORE. */
export const NAV_GROUP_IDS = ['main', 'learn', 'more'];

const NAV_GROUP_BY_ROUTE = {
  LandingPage: 'main',
  SearchPage: 'main',
  SweetSkillsPage: 'main',
  BecomeAListenerPage: 'main',
  DashboardPage: 'main',
  HowItWorksPage: 'learn',
  ForTalkersPage: 'learn',
  ForListenersPage: 'learn',
  AboutPage: 'learn',
  SafetyPage: 'learn',
  FAQPage: 'learn',
  PricingPage: 'learn',
  ContactPage: 'learn',
  PromotePage: 'more',
  GuidesPage: 'more',
  TipsPage: 'more',
};

/**
 * Which hamburger section a site route belongs to.
 *
 * @param {string} [routeName]
 * @returns {'main'|'learn'|'more'}
 */
export const navGroupForRoute = routeName => NAV_GROUP_BY_ROUTE[routeName] || 'more';

/**
 * Public hamburger links grouped MAIN / LEARN / MORE. Help is omitted so
 * the menu can pin it last. Signed-in callers can pass includeDashboard.
 *
 * @param {Object} [params]
 * @param {boolean} [params.includeDashboard]
 * @returns {Array<{ id: string, labelId: string, links: Array<{ name: string, id: string }> }>}
 */
export const getGroupedPublicNav = (params = {}) => {
  const { includeDashboard = false, ...navParams } = params;
  const links = getPublicNavLinksWithoutHelp(navParams);
  const grouped = { main: [], learn: [], more: [] };
  links.forEach(link => {
    grouped[navGroupForRoute(link.name)].push(link);
  });
  if (includeDashboard) {
    const searchIndex = grouped.main.findIndex(link => link.name === 'SearchPage');
    const insertAt = searchIndex >= 0 ? searchIndex + 1 : grouped.main.length;
    grouped.main = [
      ...grouped.main.slice(0, insertAt),
      DASHBOARD_NAV_LINK,
      ...grouped.main.slice(insertAt),
    ];
  }
  return NAV_GROUP_IDS.filter(id => grouped[id].length > 0).map(id => ({
    id,
    labelId: `SiteNav.group${id.charAt(0).toUpperCase()}${id.slice(1)}`,
    links: grouped[id],
  }));
};

const MAIN_ICON_ROUTES = new Set([
  'LandingPage',
  'SearchPage',
  'BecomeAListenerPage',
  'DashboardPage',
  'HowItWorksPage',
  'AboutPage',
  'SafetyPage',
  'FAQPage',
  'PricingPage',
  'ContactPage',
]);

/**
 * Subtle icons only on Home, Find a listener, and Dashboard.
 *
 * @param {string} [routeName]
 * @returns {boolean}
 */
export const hasMainNavIcon = routeName => MAIN_ICON_ROUTES.has(routeName);

/**
 * Public nav without Help — Help sits last, above Log out in the hamburger.
 *
 * @param {Object} [params]
 * @returns {Array<{ name: string, id: string }>}
 */
export const getPublicNavLinksWithoutHelp = (params = {}) =>
  getPublicNavLinks(params).filter(link => link.name !== 'HelpPage');

export const getPublicNavLinks = ({
  currentUser,
  currentUserHasListings = false,
  isAuthenticated,
} = {}) => {
  const signedIn = isAuthenticated === true || (isAuthenticated !== false && !!currentUser?.id);
  const withHomeTo = link =>
    link.name === 'LandingPage'
      ? { ...link, to: landingPageLinkTo({ isAuthenticated: signedIn }) }
      : link;
  // Signed-in hamburger keeps Become a listener once, next to Dashboard.
  if (signedIn) {
    return PUBLIC_NAV_LINKS.filter(link => link.name !== 'BecomeAListenerPage').map(withHomeTo);
  }
  if (showBecomeAListenerCta({ currentUser, currentUserHasListings, isAuthenticated })) {
    return PUBLIC_NAV_LINKS.filter(link => GUEST_MENU_ROUTES.has(link.name)).map(withHomeTo);
  }
  return PUBLIC_NAV_LINKS.filter(
    link => GUEST_MENU_ROUTES.has(link.name) && link.name !== 'BecomeAListenerPage'
  ).map(withHomeTo);
};

const hostedRouteName = link => link?.route?.name || null;

/** Console leftovers that duplicate the one Profile menu item. */
const PROFILE_DUPLICATE_ROUTES = new Set([
  'YourProfilePage',
  'YourProfilePageTab',
  'ProfileSettingsPage',
  'ManageListingsPage',
  'EditListingPage',
  'ProfilePage',
  'ProfilePageVariant',
  'ProfileBasePage',
]);

const PROFILE_DUPLICATE_TEXT = /listening profile|your profile|view listening|edit listening|your listings|profile settings/i;

/**
 * Hosted custom links that would pile View / Edit / Listening profile into the menu.
 *
 * @param {Object} [link]
 * @returns {boolean}
 */
export const isProfileMenuDuplicateLink = link => {
  if (PROFILE_DUPLICATE_ROUTES.has(hostedRouteName(link))) {
    return true;
  }
  const text = typeof link?.text === 'string' ? link.text : '';
  return PROFILE_DUPLICATE_TEXT.test(text);
};

/**
 * Keep Become a listener and site pages. Drop extra profile/edit items.
 *
 * @param {Array<Object>} [links]
 * @returns {Array<Object>}
 */
export const withoutProfileMenuDuplicates = (links = []) =>
  (Array.isArray(links) ? links : []).filter(link => !isProfileMenuDuplicateLink(link));

/**
 * Hosted Console customLinks plus the built-in site pages, without duplicates.
 *
 * @param {Array<Object>} hostedLinks resolved custom links from config.topbar
 * @param {{ formatMessage: Function }} intl
 * @returns {Array<Object>}
 */
export const mergeDesktopCustomLinks = (hostedLinks, intl, { isAuthenticated } = {}) => {
  const hosted = Array.isArray(hostedLinks) ? hostedLinks : [];
  const hostedNames = new Set(hosted.map(hostedRouteName).filter(Boolean));

  const siteLinks = getDesktopSiteNavLinks({ isAuthenticated })
    .filter(link => !hostedNames.has(link.name))
    .map(link => ({
      group: 'primary',
      text: intl.formatMessage({ id: link.id }),
      type: 'internal',
      route: { name: link.name, to: link.to },
    }));

  return withoutProfileMenuDuplicates([...siteLinks, ...hosted]);
};
